import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Heart, MessageCircle, Share2, MoreHorizontal, Pencil, EyeOff, Trash2,
  Send, Loader2, Clapperboard, X,
} from "lucide-react";
import { useSession } from "@/context/SessionContext";
import { socialApi, mediaUrl } from "@/api/social";
import { api } from "@/api/client";

function Avatar({ src, name }) {
  return (
    <div className="w-9 h-9 rounded-full border-2 border-charcoal overflow-hidden bg-gold grid place-items-center shrink-0">
      {src ? <img src={src} alt="" className="w-full h-full object-cover" />
        : <span className="font-mono text-[10px] font-black text-charcoal">{(name || "?").slice(0, 2).toUpperCase()}</span>}
    </div>
  );
}

function ReelVideo({ id }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    (async () => {
      try {
        const res = await api.get(`/central-core/vault-file/${id}`, { responseType: "blob" });
        url = URL.createObjectURL(res.data);
        setSrc(url);
      } catch { /* stub */ }
    })();
    return () => url && URL.revokeObjectURL(url);
  }, [id]);
  return (
    <div className="relative w-full rounded-xl border-2 border-charcoal overflow-hidden bg-black aspect-video" data-testid={`reel-frame-${id}`}>
      <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-gold text-black font-mono text-[8px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full border-2 border-charcoal">
        <Clapperboard size={10} /> Reel
      </span>
      {src
        ? <video src={src} controls playsInline className="w-full h-full object-contain" data-testid={`reel-video-${id}`} />
        : <div className="w-full h-full grid place-items-center"><Loader2 size={20} className="text-gold animate-spin" /></div>}
    </div>
  );
}

export default function PostCard({ post, onChanged }) {
  const { token, user } = useSession();
  const isOwner = post.type === "post" ? post.author_id === user?.user_id : true;
  const itemType = post.type || "post";

  const [liked, setLiked] = useState(!!post.liked);
  const [likes, setLikes] = useState(post.likes || 0);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.text || "");
  const [busy, setBusy] = useState(false);

  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [cInput, setCInput] = useState("");
  const [cCount, setCCount] = useState(post.comments || 0);
  const [cLoading, setCLoading] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenu(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const onLike = async () => {
    setLiked((v) => !v); setLikes((n) => n + (liked ? -1 : 1));
    try {
      const r = await socialApi.like(post.id, itemType);
      setLiked(r.liked); setLikes(r.likes);
    } catch { setLiked(!!post.liked); setLikes(post.likes || 0); toast.error("Could not react"); }
  };

  const onShare = async () => {
    try { await navigator.clipboard.writeText(`${window.location.origin}/#/${itemType}/${post.id}`); toast.success("Link copied"); }
    catch { toast("Share link ready", { icon: "🔗" }); }
  };

  const openComments = async () => {
    const next = !showComments; setShowComments(next);
    if (next && comments.length === 0) {
      setCLoading(true);
      try { const r = await socialApi.comments(post.id, itemType); setComments(r.comments || []); }
      finally { setCLoading(false); }
    }
  };

  const submitComment = async () => {
    if (!cInput.trim()) return;
    try {
      const r = await socialApi.addComment(post.id, cInput.trim(), itemType);
      setComments((c) => [...c, r.comment]); setCCount((n) => n + 1); setCInput("");
    } catch { toast.error("Could not comment"); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try { await socialApi.editItem(post.id, editText.trim(), itemType); toast.success("Updated"); setEditing(false); onChanged?.(); }
    catch { toast.error("Edit failed"); } finally { setBusy(false); }
  };

  const doHide = async () => {
    setMenu(false);
    try { await socialApi.hideItem(post.id, itemType); toast("Hidden from your feed", { icon: "🙈" }); onChanged?.(); }
    catch { toast.error("Could not hide"); }
  };

  const doDelete = async () => {
    setMenu(false);
    try { await socialApi.deleteItem(post.id, itemType); toast.success("Deleted"); onChanged?.(); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <article data-testid={`post-${post.id}`} className="bg-white border-2 border-charcoal rounded-2xl p-3 shadow-[3px_3px_0px_#1A1A1A] space-y-2.5 relative">
      <div className="flex items-center gap-2.5">
        <Avatar src={post.author_avatar} name={post.author_name} />
        <div className="min-w-0 flex-1">
          <p className="font-serif text-base text-charcoal leading-none truncate">{post.author_name}</p>
          <span className="font-mono text-[9px] text-charcoal/40">@{post.author_handle}{post.edited ? " · edited" : ""}</span>
        </div>
        {isOwner && (
          <div className="relative" ref={menuRef}>
            <button onClick={() => setMenu((v) => !v)} data-testid={`post-menu-${post.id}`} aria-label="Options"
              className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
              <MoreHorizontal size={16} strokeWidth={2.4} />
            </button>
            {menu && (
              <div data-testid={`post-menu-panel-${post.id}`} className="absolute right-0 top-9 z-30 w-32 bg-cream border-2 border-charcoal rounded-xl shadow-hard overflow-hidden">
                {[
                  { label: "Edit", icon: Pencil, fn: () => { setMenu(false); setEditing(true); }, tid: "post-edit" },
                  { label: "Hide", icon: EyeOff, fn: doHide, tid: "post-hide" },
                  { label: "Delete", icon: Trash2, fn: doDelete, tid: "post-delete", danger: true },
                ].map((a) => (
                  <button key={a.label} onClick={a.fn} data-testid={`${a.tid}-${post.id}`}
                    className={`w-full flex items-center gap-2 px-3 py-2 font-mono text-[11px] font-bold uppercase tracking-wider border-b border-charcoal/10 last:border-0 ${a.danger ? "text-red-600" : "text-charcoal"} hover:bg-gold/20`}>
                    <a.icon size={13} /> {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2" data-testid={`post-editor-${post.id}`}>
          <textarea value={editText} rows={3} onChange={(e) => setEditText(e.target.value)}
            className="w-full bg-white border-2 border-charcoal rounded-xl px-3 py-2 font-mono text-[13px] text-charcoal outline-none focus:ring-2 focus:ring-gold resize-none shadow-[2px_2px_0px_#1A1A1A]" />
          <div className="flex gap-2">
            <button onClick={saveEdit} disabled={busy} data-testid={`post-edit-save-${post.id}`}
              className="flex-1 bg-gold text-black font-black font-mono text-[10px] uppercase tracking-widest py-2 rounded-lg border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] flex items-center justify-center gap-1">
              {busy ? <Loader2 size={13} className="animate-spin" /> : null} Save
            </button>
            <button onClick={() => { setEditing(false); setEditText(post.text || ""); }}
              className="px-3 bg-white text-charcoal font-black font-mono text-[10px] uppercase tracking-widest py-2 rounded-lg border-2 border-charcoal"><X size={13} /></button>
          </div>
        </div>
      ) : (
        post.text && <p className="font-mono text-[13px] text-charcoal/85 leading-relaxed whitespace-pre-wrap" data-testid={`post-text-${post.id}`}>{post.text}</p>
      )}

      {post.type === "reel"
        ? <ReelVideo id={post.id} />
        : post.media?.map((m) =>
            m.kind === "image"
              ? <div key={m.id} className="w-full rounded-xl border-2 border-charcoal overflow-hidden bg-black aspect-video" data-testid={`post-media-frame-${m.id}`}>
                  <img src={mediaUrl(m.id, token)} alt="" data-testid={`post-image-${m.id}`} className="w-full h-full object-contain" />
                </div>
              : <audio key={m.id} src={mediaUrl(m.id, token)} controls data-testid={`post-audio-${m.id}`} className="w-full" />
          )}

      {/* action footer */}
      <div className="flex items-center gap-4 pt-2 border-t-2 border-charcoal/5">
        <button onClick={onLike} data-testid={`post-like-${post.id}`} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-charcoal/60 active:scale-90 transition-transform">
          <Heart size={16} strokeWidth={2.2} className={liked ? "text-red-500" : ""} fill={liked ? "currentColor" : "none"} />
          <span className={liked ? "text-red-500" : ""}>{likes}</span>
        </button>
        <button onClick={openComments} data-testid={`post-comment-${post.id}`} className="inline-flex items-center gap-1.5 font-mono text-[11px] text-charcoal/60 active:scale-90 transition-transform">
          <MessageCircle size={16} strokeWidth={2.2} /> {cCount}
        </button>
        <button onClick={onShare} data-testid={`post-share-${post.id}`} className="ml-auto inline-flex items-center gap-1.5 font-mono text-[11px] text-charcoal/60 active:scale-90 transition-transform">
          <Share2 size={16} strokeWidth={2.2} />
        </button>
      </div>

      {showComments && (
        <div className="space-y-2 pt-1" data-testid={`post-comments-${post.id}`}>
          {cLoading ? <div className="flex justify-center py-2"><Loader2 size={16} className="text-gold animate-spin" /></div>
            : comments.length === 0 ? <p className="font-mono text-[10px] text-charcoal/40 text-center py-1">Be the first to comment</p>
            : comments.map((c) => (
              <div key={c.id} className="flex items-start gap-2 bg-cream border-2 border-charcoal/15 rounded-lg p-2">
                <Avatar src={c.author_avatar} name={c.author_name} />
                <div className="min-w-0 flex-1">
                  <p className="font-mono text-[10px] font-bold text-charcoal">{c.author_name}</p>
                  <p className="font-mono text-[11px] text-charcoal/75 break-words">{c.text}</p>
                </div>
              </div>
            ))}
          <div className="flex items-center gap-2">
            <input value={cInput} onChange={(e) => setCInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitComment()}
              placeholder="Add a comment…" data-testid={`comment-input-${post.id}`}
              className="flex-1 bg-white border-2 border-charcoal rounded-lg px-3 py-1.5 font-mono text-[11px] text-charcoal outline-none focus:ring-2 focus:ring-gold shadow-[2px_2px_0px_#1A1A1A]" />
            <button onClick={submitComment} data-testid={`comment-send-${post.id}`}
              className="w-9 h-9 grid place-items-center rounded-lg border-2 border-charcoal bg-gold text-black shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none">
              <Send size={14} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
